import { useState } from "react";
import { Plus, Minus, Flame } from "lucide-react";
import { formatARS, BACKEND_URL } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import CustomizationModal from "@/components/CustomizationModal";

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function ProductCard({ product, promoLock }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const { items, addItem, increment, decrement } = useCart();
  const hasSizes = product.precioGrande != null && product.precioChica != null;
  const isPizza = ["piedra", "molde", "xl"].includes(product.categoria);
  const qty = isPizza ? 0 : (items.find((i) => i.key === product.id)?.cantidad || 0);
  const locked = promoLock && !promoLock.allowed;

  return (
    <>
      <div
        data-testid={`product-card-${product.id}`}
        onClick={() => { if (!locked && isPizza) setModalOpen(true); }}
        className={`group bg-panel border border-line hover:border-brand transition-colors duration-300 rounded-xl overflow-hidden flex flex-col ${locked ? "opacity-40 grayscale pointer-events-none select-none" : "cursor-pointer"}`}
      >
        <div className="relative aspect-[4/3] bg-black overflow-hidden">
          {promoLock?.allowed && promoLock.remaining > 0 && (
            <span data-testid={`promo-faltan-${product.id}`} className="absolute bottom-2 right-2 z-10 bg-gold text-black text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded shadow-lg">
              Faltan {promoLock.remaining}
            </span>
          )}
          <img
            key={resolveImg(product.imagenThumb || product.imagen)}
            src={resolveImg(product.imagenThumb || product.imagen)}
            alt={product.nombre}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-full object-cover group-hover:scale-105 transition-[transform,opacity] duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
          <div className="absolute top-2 left-2 flex gap-1 flex-wrap max-w-[70%]">
            {product.esPromo && (
              <span className="bg-brand text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded">Promo</span>
            )}
            {product.esCombo && !product.esPromo && (
              <span className="bg-gold text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded">Combo</span>
            )}
            {product.etiqueta && (
              <span data-testid={`product-badge-${product.id}`} className="rasta-gradient text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded">{product.etiqueta}</span>
            )}
            {product.tags?.slice(0, 1).map((t) => (
              <span key={t} className="bg-white/10 border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded">
                {t}
              </span>
            ))}
          </div>

          {qty > 0 && (
            <div data-testid={`quickstepper-${product.id}`} className="absolute inset-0 bg-black/55 flex items-center justify-center">
              <div className="pop-in flex items-center gap-3 bg-black/70 backdrop-blur border border-brand rounded-full px-2 py-1.5 shadow-[0_0_30px_-5px_rgba(255,84,0,0.6)]">
                <button
                  data-testid={`quickstepper-dec-${product.id}`}
                  onClick={(e) => { e.stopPropagation(); decrement(product.id); }}
                  className="w-9 h-9 rounded-full bg-line hover:bg-brand text-white hover:text-black flex items-center justify-center transition-colors"
                >
                  <Minus className="w-4 h-4" strokeWidth={3} />
                </button>
                <span data-testid={`quickstepper-qty-${product.id}`} className="font-heading text-2xl text-white w-8 text-center">{qty}</span>
                <button
                  data-testid={`quickstepper-inc-${product.id}`}
                  onClick={(e) => { e.stopPropagation(); increment(product.id); }}
                  className="w-9 h-9 rounded-full bg-brand hover:bg-brand-strong text-black flex items-center justify-center transition-colors"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-2.5 sm:p-4 flex flex-col flex-1">
          <h3 className="font-heading text-sm sm:text-xl uppercase text-white leading-tight">{product.nombre}</h3>
          <p className="hidden sm:block text-xs text-zinc-500 mt-1 line-clamp-2 min-h-[2rem]">{product.descripcion}</p>

          {hasSizes && (
            <div className="mt-2 sm:mt-3 flex flex-wrap gap-1 sm:gap-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-zinc-500">
              <span className="bg-black border border-line rounded px-1.5 py-0.5 sm:px-2 sm:py-1">Grande {formatARS(product.precioGrande)}</span>
              <span className="bg-black border border-line rounded px-1.5 py-0.5 sm:px-2 sm:py-1">Chica {formatARS(product.precioChica)}</span>
            </div>
          )}

          <div className="mt-2 sm:mt-4 flex items-center justify-between gap-2 pt-2 sm:pt-3 border-t border-line">
            {!hasSizes && <div className="font-heading text-lg sm:text-2xl text-gold">{formatARS(product.precio)}</div>}
            {hasSizes && <div className="font-heading text-lg sm:text-2xl text-gold">{formatARS(product.precioGrande)}</div>}
            {isPizza ? (
              <button
                data-testid={`add-to-cart-button-${product.id}`}
                onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
                className="flex items-center gap-1 bg-brand hover:bg-brand-strong text-black font-black uppercase text-[10px] sm:text-xs px-2 py-1.5 sm:px-3 sm:py-2 rounded transition-colors duration-200"
              >
                <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} /> Elegir
              </button>
            ) : qty === 0 ? (
              <button
                data-testid={`add-to-cart-button-${product.id}`}
                onClick={(e) => { e.stopPropagation(); addItem(product); }}
                className="flex items-center gap-1 bg-brand hover:bg-brand-strong text-black font-black uppercase text-[10px] sm:text-xs px-2 py-1.5 sm:px-3 sm:py-2 rounded transition-colors duration-200"
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} /> Agregar
              </button>
            ) : (
              <span data-testid={`incart-${product.id}`} className="text-[11px] uppercase tracking-widest font-black text-brand">
                {qty} en el carrito
              </span>
            )}
          </div>
        </div>
      </div>

      {modalOpen && <CustomizationModal product={product} onClose={() => setModalOpen(false)} />}
    </>
  );
}
